import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid, Card, CardMedia, CardContent, CardActions,
  Box, Typography, IconButton, Chip, Tooltip, Menu, MenuItem, ListItemIcon, ListItemText,
} from '@mui/material';
import {
  Info as InfoIcon,
  Calculate as SolveIcon,
  CloudDownload as DownloadIcon,
  Delete as DeleteIcon,
  InsertDriveFile as FileIcon,
  Science as CalibratedIcon,
  CheckCircle as ReadyIcon,
  KeyboardArrowDown as ArrowDownIcon,
  BrokenImage as MissingIcon,
} from '@mui/icons-material';
import { formatSize, filePreviewUrl } from '../../utils/fileUtils';
import MissingCalibrationChip from './MissingCalibrationChip';
import ProcessingFailureChip from './ProcessingFailureChip';

// Plate-solve status chip. In the grid the chip stands alone (no surrounding
// "Plate Solve" label), so the label/tooltip must make clear it's the *plate solve*
// that failed — not the file/upload — and why.
export const SolveChip = ({ file }) => {
  const result = file.plate_solve_result || {};
  const solver = result.solver
    ? `${result.solver}${result.solver_version ? ` (${result.solver_version})` : ''}`
    : 'the plate solver';
  const attemptedAt = result.solved_at ? new Date(result.solved_at).toLocaleString() : null;

  let label, color, variant, tip;
  if (file.plate_solve_status === 'queued') {
    label = 'Solve queued'; color = 'info'; variant = 'outlined';
    tip = 'Queued for plate solving — waiting for the processor.';
  } else if (file.plate_solve_status === 'solving') {
    label = 'Solving…'; color = 'info'; variant = 'filled';
    tip = 'Plate solving in progress…';
  } else if (file.plate_solve_status === 'solved' || result.solved === true) {
    label = 'Plate solved'; color = 'success'; variant = 'filled';
    tip = `Plate solved by ${solver}${attemptedAt ? ` at ${attemptedAt}` : ''}.`;
  } else if (file.plate_solve_status === 'failed' || (result.solved === false && result.solved_at)) {
    label = 'Solve failed'; color = 'error'; variant = 'outlined';
    tip = `Plate solving failed — ${solver} found no solution${attemptedAt ? ` (last attempt ${attemptedAt})` : ''}. `
        + 'Common for short, sparse, or simulator frames; the image file itself is fine.';
  } else {
    label = 'Not solved'; color = 'default'; variant = 'outlined';
    tip = 'Plate solving has not run for this file yet.';
  }

  return (
    <Tooltip title={tip} arrow>
      <Chip label={label} size="small" color={color} variant={variant} />
    </Tooltip>
  );
};

/**
 * Check if a file has matching master coverage based on the observatory's cached master_coverage.
 */
const DEFAULT_TEMP_TOLERANCE = 2.0;

/**
 * #1531: only LIGHT frames get calibrated. Mirror the server's authoritative guard
 * (calibrationService.ts `_calibratabilityFromParsed`): any image_type containing
 * dark/flat/bias, or exactly 'offset' (a bias synonym), is a calibration frame.
 * Lenient on missing/empty image_type (treated as light) — matches real data where
 * ~0.4% of files have no image_type and are mostly lights.
 */
export const isCalibrationFrame = (file) => {
  const it = String(file?.image_type ?? '').toLowerCase();
  // All substring (incl. 'offset') so synonyms/whitespace are caught symmetrically,
  // matching the server's isCalibrationImageType in utils/calibrationMatch.
  return it.includes('dark') || it.includes('flat') || it.includes('bias') || it.includes('offset');
};

export const hasMatchingCoverage = (file, masterCoverage) => {
  // Calibration frames (bias/dark/flat) are never themselves "calibratable" — don't match
  // them against dark/flat masters (a 0 s bias would otherwise floor to 1 s and match). (#1531)
  if (isCalibrationFrame(file)) return false;
  if (!masterCoverage || !file.observatory_id) return false;
  const coverage = masterCoverage[file.observatory_id?.toString?.() || file.observatory_id];
  if (!coverage || coverage.length === 0) return false;

  const fileGain = parseFloat(file.gain) || 0;
  const fileBinning = file.binning || '1x1';
  const fileCameraName = file.camera_name || '';
  const fileTemp = file.temperature != null ? parseFloat(file.temperature) : null;
  // #1518/#1516: darks are keyed on a whole-second exposure floored to a 1 s minimum on the server
  // (a sub-second light demands a 1 s dark, never 0 s). Mirror that here — `Math.max(1, round)` —
  // so a sub-second light (e.g. 0.1 s) matches the coverage doc's `dark_exposure_times: [1]` instead
  // of looking for a 0 s entry that no longer exists. Only used for dark matching below (flats match
  // by filter), so flooring is safe.
  const fileExposure = file.exposure_time != null ? Math.max(1, Math.round(parseFloat(file.exposure_time))) : null;

  // Temperature set points are per-camera, not per-binning/gain — check once before the loop
  if (fileTemp != null && fileCameraName) {
    const anyCameraEntry = coverage.find(c => c.camera_name === fileCameraName);
    if (anyCameraEntry) {
      const tolerance = anyCameraEntry.temperature_tolerance || DEFAULT_TEMP_TOLERANCE;
      const setPoints = anyCameraEntry.temperature_set_points;
      if (setPoints?.length > 0) {
        const nearSetPoint = setPoints.some(sp => Math.abs(fileTemp - sp) <= tolerance);
        if (!nearSetPoint) return false;
      }
    }
  }

  return coverage.some(c => {
    const tolerance = c.temperature_tolerance || DEFAULT_TEMP_TOLERANCE;
    if (fileCameraName && c.camera_name !== fileCameraName) return false;
    if (c.gain !== fileGain) return false;
    if (c.binning !== fileBinning) return false;
    if (!c.has_dark) return false;
    // Check dark temperature range — file temp must be within tolerance of available darks
    if (fileTemp != null && c.dark_temp_min != null && c.dark_temp_max != null) {
      if (fileTemp < c.dark_temp_min - tolerance || fileTemp > c.dark_temp_max + tolerance) return false;
    }
    // Check dark exposure time — rounded integer match (CMOS behavior)
    if (fileExposure != null && c.dark_exposure_times?.length > 0) {
      if (!c.dark_exposure_times.includes(fileExposure)) return false;
    }
    // Flat is always required — check for matching flat by filter
    if (file.filter) {
      if (!c.filters_with_flats?.includes(file.filter)) return false;
    } else {
      // No filter on file — need an unfiltered flat
      if (!c.has_unfiltered_flat) return false;
    }
    return true;
  });
};

const MastersAvailableChip = ({ file, masterCoverage }) => {
  if (!hasMatchingCoverage(file, masterCoverage)) return null;
  return (
    <Tooltip title="Matching calibration masters available">
      <Chip
        icon={<ReadyIcon />}
        label="Cal Ready"
        size="small"
        color="success"
        variant="outlined"
      />
    </Tooltip>
  );
};

const FileGrid = ({ files, onPreview, onSolve, getDownloadUrl, getCalibratedDownloadUrl, downloadCalibrated, onCalibrateError, onDelete, canDelete, masterCoverage }) => {
  const navigate = useNavigate();
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);
  const [downloadMenuFileId, setDownloadMenuFileId] = useState(null);

  if (!files || files.length === 0) return null;

  const handleDownloadMenuOpen = (event, fileId) => {
    setDownloadMenuAnchor(event.currentTarget);
    setDownloadMenuFileId(fileId);
  };

  const handleDownloadMenuClose = () => {
    setDownloadMenuAnchor(null);
    setDownloadMenuFileId(null);
  };

  return (
    <>
      <Grid container spacing={2} sx={{ p: 2 }}>
        {files.map((file) => {
          const fileId = file.file_id || file._id;
          const fileName = file.file_name || file.filename;
          const thumbnailUrl = filePreviewUrl(file, { thumbnail: true });
          const previewUrl = filePreviewUrl(file);
          const showDelete = onDelete && (!canDelete || canDelete(file));
          const isFileMissing = file.file_status === 'file_missing';
          const calReady = !isFileMissing && hasMatchingCoverage(file, masterCoverage);

          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={fileId}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', ...(isFileMissing && { opacity: 0.7, border: '1px solid', borderColor: 'error.dark' }) }}>
                {thumbnailUrl ? (
                  <CardMedia
                    // Remount on updated_at change so a rebuilt preview
                    // actually repaints — the Grid key is stable on
                    // file_id (#614).
                    key={file.updated_at || thumbnailUrl}
                    component="img"
                    height="140"
                    image={thumbnailUrl}
                    alt={fileName}
                    loading="lazy"
                    sx={{
                      objectFit: 'contain',
                      bgcolor: 'grey.900',
                      cursor: 'pointer',
                    }}
                    onClick={() => previewUrl && onPreview?.(previewUrl, fileName)}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <Box
                    sx={{
                      height: 140,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.800',
                      cursor: previewUrl ? 'pointer' : 'default',
                    }}
                    onClick={() => previewUrl && onPreview?.(previewUrl, fileName)}
                  >
                    <FileIcon sx={{ fontSize: 48, color: 'grey.500' }} />
                  </Box>
                )}

                <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                  <Tooltip title={fileName} placement="top">
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        cursor: 'pointer',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                      onClick={() => navigate(`/files/file/${fileId}`)}
                    >
                      {fileName}
                    </Typography>
                  </Tooltip>
                  <Typography variant="caption" color="text.secondary" component="div">
                    {formatSize(file.file_size_bytes || file.file_size)}
                    {file.filter ? ` · ${file.filter}` : ''}
                    {file.exposure_time ? ` · ${file.exposure_time}s` : ''}
                    {file.gain != null ? ` · Gain ${file.gain}` : ''}
                  </Typography>
                  <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {isFileMissing && (
                      <Tooltip title="FITS file is missing from storage — metadata preserved">
                        <Chip icon={<MissingIcon />} label="File Missing" size="small" color="error" variant="outlined" />
                      </Tooltip>
                    )}
                    <ProcessingFailureChip file={file} />
                    {!isFileMissing && <SolveChip file={file} />}
                    <MastersAvailableChip file={file} masterCoverage={masterCoverage} />
                    {/* #1531: MissingCalibrationChip is a no-op today in the grid (group-files omits
                        `calibratability`, so it renders null), but keep the calibration-frame guard so
                        a bias/dark/flat never shows "Missing Calibration" if that field is ever added here. */}
                    {!isFileMissing && !calReady && !isCalibrationFrame(file) && <MissingCalibrationChip file={file} />}
                  </Box>
                </CardContent>

                <CardActions sx={{ pt: 0 }}>
                  <IconButton size="small" color="info" onClick={() => navigate(`/files/file/${fileId}`)} title="Details">
                    <InfoIcon fontSize="small" />
                  </IconButton>
                  {!isFileMissing && (
                    <IconButton size="small" color="secondary" onClick={() => onSolve?.(fileId)} title="Plate Solve">
                      <SolveIcon fontSize="small" />
                    </IconButton>
                  )}
                  {!isFileMissing && calReady && getCalibratedDownloadUrl ? (
                    <IconButton
                      size="small"
                      color="success"
                      onClick={(e) => handleDownloadMenuOpen(e, fileId)}
                      title="Download Options"
                    >
                      <CalibratedIcon fontSize="small" />
                      <ArrowDownIcon sx={{ fontSize: 14, ml: -0.5 }} />
                    </IconButton>
                  ) : !isFileMissing ? (
                    // #269: no target="_blank". The download URL is same-origin
                    // (/api/v1/user-files/download/:fileId) and the server sends
                    // Content-Disposition: attachment, so the browser downloads without
                    // navigating — opening a tab just made one flash up and close.
                    // The `download` attribute is a belt-and-braces hint; it works here
                    // because the URL is same-origin (it did not when these were
                    // cross-origin presigned B2 URLs, which is what the issue hit).
                    <IconButton
                      size="small"
                      color="primary"
                      component="a"
                      href={getDownloadUrl?.(fileId)}
                      download={fileName}
                      title="Download"
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  ) : null}
                  {showDelete && (
                    <IconButton size="small" color="error" onClick={() => onDelete(fileId, fileName)} title="Delete">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>
      <Menu
        anchorEl={downloadMenuAnchor}
        open={Boolean(downloadMenuAnchor)}
        onClose={handleDownloadMenuClose}
      >
        <MenuItem onClick={() => {
          if (downloadMenuFileId) {
            const a = document.createElement('a');
            a.href = getDownloadUrl?.(downloadMenuFileId);
            a.click();
          }
          handleDownloadMenuClose();
        }}>
          <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Download Raw</ListItemText>
        </MenuItem>
        <MenuItem onClick={async () => {
          if (downloadMenuFileId && downloadCalibrated) {
            handleDownloadMenuClose();
            const result = await downloadCalibrated(downloadMenuFileId);
            if (!result.success && onCalibrateError) {
              onCalibrateError(result.error);
            }
          } else {
            handleDownloadMenuClose();
          }
        }}>
          <ListItemIcon><CalibratedIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Download Calibrated</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default FileGrid;
